import { useEffect, useState } from "react";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail, fmtMoney, logUndoEvent } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardHeader } from "@/components/shared";
import UndoCountdownChip, { setExtendThreshold } from "@/components/UndoCountdownChip";
import MiniSparklineSVG from "@/components/MiniSparklineSVG";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { EnvelopeSimple, CheckCircle, WarningCircle, Info, FloppyDisk, CalendarBlank, PaperPlaneTilt, Broom, CaretDown, CaretUp, ArrowsClockwise, Eye } from "@phosphor-icons/react";
import { Link as LinkIcon, Check as CheckIcon, Download, Pin as PinIcon, PinOff as PinOffIcon } from "lucide-react";
import { toast } from "sonner";
import { playChime, playFlipChime, playBothChimes, playBothChimesReverse, getChimeVolume, setChimeVolume, isChimeMuted, setChimeMuted } from "@/lib/chime";
import UndoActionLabel from "@/components/UndoActionLabel";
import SeedInputPanel from "@/components/SeedInputPanel";
import ThresholdHistoryCard from "@/components/ThresholdHistoryCard";
import RateLimitDashboardCard from "@/components/RateLimitDashboardCard";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import WeeklyRecapPreviewModal from "@/components/WeeklyRecapPreviewModal";

/**
 * Iter 76 — UndoActionLabel extracted to /app/frontend/src/components/UndoActionLabel.jsx
 * (with sessionStorage-backed pause counter). Imported above.
 */

export default function NotificationsPage() {
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [data, setData] = useState({
    notifications: [], email_configured: false,
    sender_email: "", recipient_email: "",
    default_sender_email: "", custom_sender: false, custom_recipient: false,
    failed_count: 0,
  });
  const [busy, setBusy] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settings, setSettings] = useState({ sender_email: "", recipient_email: "", mood_digest_opt_in: true });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [digest, setDigest] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [recapOpen, setRecapOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(Date.now());
  const [_tick, _setTick] = useState(0); // force re-render every 15s to update relative time
  // Iter 49 — lifted from NextDigestHint so the parent Card can tint its
  // header when the countdown flips urgent (< 6h to send).
  const [digestTone, setDigestTone] = useState("normal");
  // Iter 53 — chime volume state. Persisted to localStorage via
  // setChimeVolume; playChime reads the current value on every play.
  const [chimeVolPct, setChimeVolPct] = useState(() => Math.round(getChimeVolume() * 100));
  // Iter 54 — mirror the global chime-muted flag so admins can silence
  // the audio cue right from the volume section without hunting for
  // the sidebar toggle. State is initialised from localStorage.
  const [chimeMuted, setChimeMutedState] = useState(() => isChimeMuted());
  // Iter 65 — Chime Loop Toggle. When true, clicking Play both / Play
  // both (reverse) starts a repeating interval (~1500ms) so admins can
  // rehearse the pattern continuously while comparing to real events.
  // A Stop button appears while a loop is active; toggling the switch
  // off clears any running loop.
  const [chimeLoop, setChimeLoop] = useState(false);
  const [chimeLoopActive, setChimeLoopActive] = useState(null); // "forward" | "reverse" | null
  const chimeLoopRef = React.useRef(null);
  // Iter 69 — Focus Ring Cue. Flag flips true for ~500ms when the
  // chime block gains focus so the presets render an amber pulse
  // that shows admins where the keyboard shortcuts (1/2/3) will land.
  const [chimeFocusPulse, setChimeFocusPulse] = useState(false);
  // Iter 71 — Reduced-Motion Fallback Cue. Separate flag so admins on
  // ``prefers-reduced-motion: reduce`` still see a subtle 500ms
  // outline (no ring animation) telling them where the keyboard
  // shortcuts will land.
  const [chimeFocusPulseFallback, setChimeFocusPulseFallback] = useState(false);
  const chimeFocusPulseTimerRef = React.useRef(null);
  // Iter 72 — Fallback Cue Prefer-Reduced-Motion Persistence. Cache
  // the MediaQueryList in a ref on mount + subscribe to `change`
  // events so the focus handler doesn't re-query the browser and the
  // outline updates live if the user toggles the OS motion setting.
  const [reducedMotion, setReducedMotion] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e) => setReducedMotion(!!e.matches);
    // Modern browsers use addEventListener; older Safari uses addListener.
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else if (mql.addListener) mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, []);
  // Iter 66 — Chime Loop Interval Slider. Persisted per-device tempo
  // (in milliseconds) that admins can tune to match different real-
  // world event rhythms. Clamped 500-3000ms.
  const [chimeLoopIntervalMs, setChimeLoopIntervalMs] = useState(() => {
    try {
      const raw = localStorage.getItem("stratos.chime.loop-interval-ms");
      const n = parseInt(raw, 10);
      if (Number.isFinite(n) && n >= 500 && n <= 3000) return n;
    } catch {}
    return 1500;
  });
  const stopChimeLoop = () => {
    if (chimeLoopRef.current) {
      clearInterval(chimeLoopRef.current);
      chimeLoopRef.current = null;
    }
    setChimeLoopActive(null);
  };
  // Iter 67 — Chime Loop Helper Extract. Single source of truth for
  // starting a chime-loop interval so both Play-both handlers and the
  // slider-restart branch share one implementation. Sets ``active`` to
  // the correct direction so the slider knows which fn to reuse when
  // restarting the interval at a new tempo. Also self-clears when the
  // global mute flag flips on mid-loop.
  const startChimeLoop = (fn, ms, direction) => {
    if (chimeLoopRef.current) clearInterval(chimeLoopRef.current);
    chimeLoopRef.current = window.setInterval(() => {
      if (isChimeMuted()) { stopChimeLoop(); return; }
      fn();
    }, ms);
    setChimeLoopActive(direction);
  };
  // Iter 67 — Shared tempo mutation. Used by both the Slider and the
  // Loop Interval Presets so the state update, localStorage
  // persistence, and running-loop restart happen in one place.
  const applyChimeLoopInterval = (n) => {
    const clamped = Math.max(500, Math.min(3000, Number(n) || 1500));
    setChimeLoopIntervalMs(clamped);
    try { localStorage.setItem("stratos.chime.loop-interval-ms", String(clamped)); } catch {}
    if (chimeLoopRef.current && chimeLoopActive) {
      const fn = chimeLoopActive === "reverse" ? playBothChimesReverse : playBothChimes;
      startChimeLoop(fn, clamped, chimeLoopActive);
    }
  };
  useEffect(() => () => stopChimeLoop(), []);
  // Iter 51 — audible cue when the tone transitions warning → urgent so
  // admins hear the transition even if the tab is backgrounded. Guarded
  // by the shared chime-mute flag so muted users stay silent.
  const prevDigestToneRef = React.useRef("normal");
  useEffect(() => {
    const prev = prevDigestToneRef.current;
    if (prev !== "urgent" && digestTone === "urgent") {
      // playChime() itself no-ops when muted or when AudioContext isn't
      // available — no need to guard here.
      playChime();
    }
    prevDigestToneRef.current = digestTone;
  }, [digestTone]);

  useEffect(() => {
    const t = setInterval(() => _setTick(x => x + 1), 15000);
    return () => clearInterval(t);
  }, []);

  const refreshNow = async () => {
    setRefreshing(true);
    try {
      await Promise.all([load(), loadDigest()]);
      setLastRefreshedAt(Date.now());
      toast.success("Refreshed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Refresh failed");
    } finally { setRefreshing(false); }
  };

  const [notifFilter, setNotifFilter] = useState("all"); // all | trades | digest
  const [archivingBotId, setArchivingBotId] = useState(null); // bot currently being archived from a deep_pause row
  const [archivedBotIds, setArchivedBotIds] = useState(new Set()); // bots successfully archived this session
  // Iter 52 — Flipper Filter Chip. When set (admin-only), the notifications
  // table is replaced by the target user's history via /admin/notifications.
  // Cleared via the X on the inline chip.
  const [flipperFilter, setFlipperFilter] = useState(null); // { user_id, email_masked } | null
  const [adminNotifications, setAdminNotifications] = useState(null);
  // Iter 53 — Filter Chip Deep-Link. Preserve the flipper filter in the
  // URL (?viewing=<uid>) so admins can share the filtered view via
  // bookmark. Auto-hydrates on mount when admin lands with the param.
  const [searchParams, setSearchParams] = useSearchParams();
  // Iter 55 — Copy Link Feedback. Toggle to `true` briefly on successful
  // clipboard write so the button shows "Copied ✓" (green) inline —
  // complements the sonner toast for admins who miss the pop-up.
  const [linkCopied, setLinkCopied] = useState(false);

  const applyFlipperFilter = async (flipper, { skipUrl = false } = {}) => {
    setFlipperFilter({ user_id: flipper.user_id, email_masked: flipper.email_masked });
    try {
      const r = await api.get(`/admin/notifications?user_id=${encodeURIComponent(flipper.user_id)}&limit=50`);
      setAdminNotifications(r.data.notifications || []);
      // Iter 53 — trust the server's masked email over whatever the caller
      // passed (deep-link hydration uses a "…" placeholder). Falls back to
      // the caller-supplied value if the server omitted the field.
      if (r.data.email_masked) {
        setFlipperFilter({ user_id: flipper.user_id, email_masked: r.data.email_masked });
      }
      // Iter 52 — the recent-flippers feed lives below the notifications
      // table, so once the filter is applied we scroll the table into
      // view. Deferred to the next tick so the newly-rendered chip
      // header exists before the scroll fires.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="notifications-table-card"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
      // Iter 53 — mirror to URL. `skipUrl` is used by the hydrate-from-URL
      // effect to avoid a redundant round-trip.
      if (!skipUrl) {
        const next = new URLSearchParams(searchParams);
        next.set("viewing", flipper.user_id);
        setSearchParams(next, { replace: false });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Filter failed");
      setFlipperFilter(null);
    }
  };

  const clearFlipperFilter = () => {
    setFlipperFilter(null);
    setAdminNotifications(null);
    // Iter 53 — strip the deep-link param when the chip is cleared.
    const next = new URLSearchParams(searchParams);
    if (next.has("viewing")) {
      next.delete("viewing");
      setSearchParams(next, { replace: false });
    }
  };

  // Iter 53 — hydrate from ?viewing=<uid> when an admin lands with the
  // param set. Runs once after isAdmin becomes true. Guarded so the
  // effect doesn't re-fetch every time the URL echoes back its own
  // updates.
  useEffect(() => {
    if (!isAdmin) return;
    const uid = searchParams.get("viewing");
    if (!uid) return;
    if (flipperFilter && flipperFilter.user_id === uid) return; // already applied
    // We don't have the masked email until the API responds — use a
    // placeholder that gets overwritten by the server response.
    applyFlipperFilter({ user_id: uid, email_masked: "…" }, { skipUrl: true })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, searchParams.get("viewing")]);

  const archiveBot = async (botId, symbol) => {
    if (!botId) return;
    setArchivingBotId(botId);
    try {
      await api.post(`/bots/${botId}/archive`);
      setArchivedBotIds((s) => new Set(s).add(botId));
      toast.success(`${symbol || "Bot"} archived — history preserved, scheduler will skip it`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Archive failed");
    } finally {
      setArchivingBotId(null);
    }
  };

  const load = () => api.get("/notifications").then(r => {
    // If the latest notification is a fresh weekly_digest we haven't seen, refresh preview too
    const prevLatestDigestAt = data?.notifications
      ?.find(n => n.kind === "weekly_digest")?.at;
    const newLatestDigestAt = (r.data.notifications || [])
      .find(n => n.kind === "weekly_digest")?.at;
    setData(r.data);
    setLastRefreshedAt(Date.now());
    if (newLatestDigestAt && newLatestDigestAt !== prevLatestDigestAt) {
      loadDigest();
    }
    // Iter 82 — Notification Badge. Clear the sidebar sentiment_flip
    // dot on every visit to the notifications page — this is the
    // canonical "read watermark" moment.
    api.post("/notifications/mark-read?kind=sentiment_flip").catch(() => {});
  }).catch(() => {});
  const loadSettings = () => api.get("/settings/notifications").then(r => {
    setSettings({
      sender_email: r.data.sender_email || "",
      recipient_email: r.data.recipient_email || "",
      mood_digest_opt_in: r.data.mood_digest_opt_in !== false,
    });
    setSettingsLoaded(true);
  }).catch(() => setSettingsLoaded(true));
  const loadDigest = () => api.get("/notifications/digest/preview").then(r => setDigest(r.data)).catch(() => {});

  useEffect(() => {
    load(); loadSettings(); loadDigest();
    let t = setInterval(() => {
      // Only poll while tab is visible; document may be undefined in SSR
      if (typeof document === "undefined" || document.visibilityState === "visible") load();
    }, 15000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") load(); // immediate refresh on tab return
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    // Iter 50 — capture the previous server values BEFORE the PUT so the
    // Undo action can roll everything back atomically. Falls back to
    // empty defaults if the last load hasn't populated yet.
    const previous = {
      sender_email: data.custom_sender ? data.sender_email : "",
      recipient_email: data.custom_recipient ? data.recipient_email : "",
      mood_digest_opt_in: settings.mood_digest_opt_in,
    };
    try {
      await api.put("/settings/notifications", {
        sender_email: settings.sender_email,
        recipient_email: settings.recipient_email || null,
        mood_digest_opt_in: settings.mood_digest_opt_in,
      });
      toast.success("Notification settings saved", {
        duration: 8000,
        description: <UndoCountdownChip duration={8000} kind="save_settings" />,
        action: {
          label: "Undo",
          onClick: async () => {
            logUndoEvent("clicked", "save_settings");
            try {
              await api.put("/settings/notifications", {
                sender_email: previous.sender_email,
                recipient_email: previous.recipient_email || null,
                mood_digest_opt_in: previous.mood_digest_opt_in,
              });
              setSettings({ ...settings, ...previous });
              load(); loadSettings();
              toast.success("Reverted");
            } catch {
              toast.error("Undo failed");
            }
          },
        },
      });
      logUndoEvent("shown", "save_settings");
      load(); loadSettings();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSavingSettings(false); }
  };

  const clearSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/settings/notifications", { sender_email: "", recipient_email: null });
      setSettings({ sender_email: "", recipient_email: "", mood_digest_opt_in: true });
      toast.success("Reset to defaults");
      load();
    } catch { toast.error("Reset failed"); }
    finally { setSavingSettings(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      await api.post("/notifications/test");
      toast.success("Test email sent — check your inbox");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally { setBusy(false); }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      await api.post("/notifications/digest/send");
      toast.success("Weekly digest sent");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally { setSendingDigest(false); }
  };

  const purgeFailed = async () => {
    setPurging(true);
    try {
      const { data: r } = await api.delete("/notifications/failed");
      toast.success(`Deleted ${r.deleted} failed notifications`);
      setPurgeOpen(false);
      load();
    } catch { toast.error("Purge failed"); }
    finally { setPurging(false); }
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="notifications-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Terminal / Alerts</div>
          <h1 className="heading text-4xl font-black tracking-tighter mt-2 flex items-center gap-3">
            <EnvelopeSimple size={30} weight="regular" className="text-[#00C805]" />
            Notifications
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            Email alerts fire when an automated bot executes a paper trade on your behalf.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <Button
            onClick={refreshNow} disabled={refreshing}
            data-testid="refresh-now-btn"
            variant="outline"
            className="border-zinc-700 bg-transparent hover:bg-zinc-900 rounded-sm h-10 mono text-[10px] uppercase tracking-widest gap-1.5"
          >
            <ArrowsClockwise size={12} weight="bold" className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing…" : "Refresh now"}
          </Button>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest" data-testid="last-refreshed">
            Updated {relativeTime(lastRefreshedAt)}
          </div>
        </div>
      </div>

      {/* Config status */}
      <Card className="p-5">
        <div className="flex items-start gap-3">
          {data.email_configured ? (
            <CheckCircle size={22} weight="fill" className="text-[#00C805] mt-0.5" />
          ) : (
            <WarningCircle size={22} weight="fill" className="text-amber-500 mt-0.5" />
          )}
          <div className="flex-1">
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Email delivery</div>
            <div className="mono text-lg mt-1" data-testid="email-status">
              {data.email_configured ? "Configured & ready" : "Not configured"}
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              {data.email_configured
                ? <>Effective sender: <span className="text-zinc-300">{data.sender_email}</span> → <span className="text-zinc-300">{data.recipient_email}</span></>
                : "Set RESEND_API_KEY in /app/backend/.env then restart backend."}
            </div>
          </div>
          {data.email_configured && (
            <Button
              onClick={sendTest} disabled={busy}
              data-testid="send-test-email-btn"
              className="bg-white text-black hover:bg-zinc-200 rounded-sm h-9 mono text-[10px] uppercase tracking-widest"
            >
              {busy ? "…" : "Send test email"}
            </Button>
          )}
        </div>
      </Card>

      {!data.email_configured && (
        <Card className="p-5 border-amber-800/40 bg-amber-950/10">
          <div className="flex gap-3">
            <Info size={20} weight="regular" className="text-amber-500 mt-0.5" />
            <div className="text-sm text-zinc-300">
              <div className="font-semibold text-amber-200">Enable email alerts in 3 steps</div>
              <ol className="mt-3 space-y-2 text-xs text-zinc-400 mono list-decimal list-inside">
                <li>Create a Resend account at <span className="text-white">resend.com</span> → API Keys → Create</li>
                <li>Paste the key into <span className="text-white">/app/backend/.env</span> as <span className="text-white">RESEND_API_KEY="re_..."</span></li>
                <li>Run <span className="text-white">sudo supervisorctl restart backend</span></li>
              </ol>
            </div>
          </div>
        </Card>
      )}

      {/* Settings form */}
      <Card>
        <CardHeader title="Sender & recipient" subtitle="Override the default sender and where alerts get delivered. Leave blank to use defaults." />
        <div className="p-5 space-y-4">
          <div>
            <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Sender email (from)</Label>
            <Input
              value={settings.sender_email}
              onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
              placeholder={data.default_sender_email || "onboarding@resend.dev"}
              data-testid="settings-sender-input"
              className="bg-zinc-950 border-zinc-700 rounded-sm mono mt-1.5 h-10"
            />
            <div className="text-[11px] text-zinc-500 mt-1.5 mono">
              Format: <span className="text-zinc-300">Name &lt;email@yourdomain.com&gt;</span>. Domain must be verified in Resend for custom addresses.
            </div>
          </div>
          <div>
            <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Recipient email (to)</Label>
            <Input
              type="email"
              value={settings.recipient_email}
              onChange={(e) => setSettings({ ...settings, recipient_email: e.target.value })}
              placeholder={data.recipient_email}
              data-testid="settings-recipient-input"
              className="bg-zinc-950 border-zinc-700 rounded-sm mono mt-1.5 h-10"
            />
            <div className="text-[11px] text-zinc-500 mt-1.5 mono">
              Defaults to your account email. In Resend test mode only your verified account email is deliverable.
            </div>
          </div>
          <div className="flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2.5">
            <div className="pr-4">
              <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Include mood shifts in weekly digest</div>
              <div className="text-xs text-zinc-400 mt-1 leading-snug">
                Adds a "Biggest mood shifts" block with the top 3 sentiment swings across your watchlist and bots.
              </div>
            </div>
            <Switch
              checked={!!settings.mood_digest_opt_in}
              disabled={!settingsLoaded}
              onCheckedChange={async (v) => {
                const previous = settings.mood_digest_opt_in;
                setSettings({ ...settings, mood_digest_opt_in: v });
                try {
                  await api.put("/settings/notifications", { mood_digest_opt_in: v });
                  // Iter 49 — offer an Undo action so an accidental toggle
                  // can be reverted from the toast itself. Undo flips both
                  // the local state and the backend value back to `previous`.
                  toast.success(v
                    ? "Mood shifts section enabled"
                    : "Mood shifts section will be omitted from your digest", {
                    duration: 8000,
                    description: <UndoCountdownChip duration={8000} kind="mood_digest" />,
                    action: {
                      label: "Undo",
                      onClick: async () => {
                        logUndoEvent("clicked", "mood_digest");
                        setSettings((s) => ({ ...s, mood_digest_opt_in: previous }));
                        try {
                          await api.put("/settings/notifications", { mood_digest_opt_in: previous });
                          toast.success("Reverted");
                        } catch {
                          // Roll UI back to `v` because the revert failed.
                          setSettings((s) => ({ ...s, mood_digest_opt_in: v }));
                          toast.error("Undo failed");
                        }
                      },
                    },
                  });
                  logUndoEvent("shown", "mood_digest");
                } catch {
                  toast.error("Save failed");
                  // Roll back on error so the UI matches server state
                  setSettings({ ...settings, mood_digest_opt_in: !v });
                }
              }}
              data-testid="mood-digest-toggle"
              className="data-[state=checked]:bg-[#00C805]"
            />
          </div>
          {/* Iter 52 — Chime Preview button. Admins receive an audio cue
              (playChime) when the weekly-digest countdown flips
              warning → urgent. Since that transition happens at most
              once a week, most admins never hear the sound. This lets
              them preview it before the first real event so they can
              recognise it. Gated on isAdmin so regular users don't see
              a button that has no effect for them. */}
          {isAdmin && (
            <div
              data-testid="chime-block"
              tabIndex={0}
              onFocus={() => {
                // Iter 69 — Focus Ring Cue. 500ms pulse around the
                // preset buttons. Ignored if a nested element (e.g.,
                // the loop-toggle checkbox) is what actually took
                // focus — we only pulse when the outer block itself
                // is the focus target.
                // Iter 71 — Reduced-Motion Fallback Cue. If the user
                // has prefers-reduced-motion: reduce, swap the ring
                // animation for a plain 500ms outline so keyboard
                // discovery still works without motion.
                if (chimeFocusPulseTimerRef.current) clearTimeout(chimeFocusPulseTimerRef.current);
                // Iter 72 — read the cached reduced-motion flag from
                // state so we don't re-query the browser on every
                // focus; the media query change listener keeps it in
                // sync with the OS setting.
                if (reducedMotion) {
                  setChimeFocusPulseFallback(true);
                } else {
                  setChimeFocusPulse(true);
                }
                chimeFocusPulseTimerRef.current = window.setTimeout(() => {
                  setChimeFocusPulse(false);
                  setChimeFocusPulseFallback(false);
                  chimeFocusPulseTimerRef.current = null;
                }, 500);
              }}
              onKeyDown={(e) => {
                // Iter 68 — Loop Presets Keyboard. When the chime block
                // contains focus, keys 1 / 2 / 3 jump the loop tempo to
                // the 0.5s / 1s / 2s presets so power users can rehearse
                // without reaching for the mouse. Ignored when a text
                // input inside the block has focus (so the seed values
                // input keeps normal typing behaviour).
                if (e.target && (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")) return;
                const map = { "1": 500, "2": 1000, "3": 2000 };
                const next = map[e.key];
                if (next) {
                  e.preventDefault();
                  applyChimeLoopInterval(next);
                }
              }}
              className="flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2.5 gap-4 focus:outline-none focus:border-amber-500/40 focus-within:border-zinc-700"
            >
              <div className="pr-4">
                <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Urgent chime preview (admin)</div>
                <div className="text-xs text-zinc-400 mt-1 leading-snug">
                  Plays the 2-note chime that fires when the digest send-window drops under 6h. Muted for users with chime_muted=true.
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {/* Iter 54 — mute switch. Wires straight into the shared
                    localStorage flag so a mute here silences all chimes
                    everywhere (SSE toasts, urgent transition, etc.). */}
                <div className="flex items-center gap-2">
                  <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">Mute</span>
                  <Switch
                    checked={chimeMuted}
                    data-testid="chime-mute-toggle"
                    onCheckedChange={(v) => {
                      setChimeMutedState(v);
                      setChimeMuted(v);
                    }}
                    className="data-[state=checked]:bg-red-500"
                  />
                </div>
                {/* Iter 53 — volume slider. Live-updates the persisted
                    volume so future chimes (real + preview) respect the
                    tuned amplitude. Shared-workspace safe.
                    Iter 54 — disabled + dimmed when muted so the two
                    controls stay visually consistent. */}
                <div className={`flex flex-col items-end gap-1 w-32 ${chimeMuted ? "opacity-40" : ""}`}>
                  <div className="mono text-[9px] text-zinc-500 uppercase tracking-widest w-full flex justify-between">
                    <span>Volume</span>
                    <span data-testid="chime-volume-value" className="text-zinc-300 tabular-nums">{chimeVolPct}%</span>
                  </div>
                  <Slider
                    data-testid="chime-volume-slider"
                    min={0} max={100} step={5}
                    value={[chimeVolPct]}
                    disabled={chimeMuted}
                    onValueChange={(v) => {
                      const n = Array.isArray(v) ? v[0] : v;
                      setChimeVolPct(n);
                      setChimeVolume(n);
                    }}
                    className="w-full"
                  />
                </div>
                <Button
                  type="button"
                  data-testid="chime-preview-btn"
                  disabled={chimeMuted}
                  onClick={() => {
                    playChime();
                    toast.success("Chime played", {
                      description: "If you heard nothing, check your chime-muted setting in the sidebar.",
                      duration: 4000,
                    });
                  }}
                  variant="outline"
                  className="border-zinc-700 bg-transparent hover:bg-zinc-900 rounded-sm h-8 mono text-[10px] uppercase tracking-widest gap-1.5 disabled:opacity-50"
                >
                  <PaperPlaneTilt size={12} weight="bold" /> Test chime
                </Button>
                {/* Iter 61 — Flip Chime Preview. Sits next to Test chime so
                    admins can rehearse the ASCENDING sign-flip cue
                    (playFlipChime, C5→E5) alongside the descending
                    urgent chime (playChime, E5→C5). Same mute/volume
                    behaviour as the primary preview button. */}
                <Button
                  type="button"
                  data-testid="flip-chime-preview-btn"
                  disabled={chimeMuted}
                  onClick={() => {
                    playFlipChime();
                    toast.success("Flip chime played", {
                      description: "Ascending 2-note cue that fires when a since-visit delta flips sign.",
                      duration: 4000,
                    });
                  }}
                  variant="outline"
                  className="border-zinc-700 bg-transparent hover:bg-zinc-900 rounded-sm h-8 mono text-[10px] uppercase tracking-widest gap-1.5 disabled:opacity-50"
                >
                  <CaretUp size={12} weight="bold" /> Test flip chime
                </Button>
                {/* Iter 62 — Chime Test Comparison. Fires the descending
                    urgent chime then the ascending sign-flip chime
                    ~600ms apart so admins hear the contrast in one
                    tap. Uses the shared mute/volume settings. */}
                <Button
                  type="button"
                  data-testid="chime-play-both-btn"
                  disabled={chimeMuted}
                  onClick={() => {
                    stopChimeLoop(); // clear any prior loop before starting
                    playBothChimes();
                    if (chimeLoop) {
                      startChimeLoop(playBothChimes, chimeLoopIntervalMs, "forward");
                    }
                    toast.success(chimeLoop ? "Looping both chimes" : "Playing both chimes", {
                      description: chimeLoop
                        ? `↓ urgent · ↑ flip — repeating every ${(chimeLoopIntervalMs / 1000).toFixed(1)}s. Click Stop to end.`
                        : "↓ urgent · ↑ flip — descending fires first, ascending ~600ms later.",
                      duration: chimeLoop ? 3000 : 4000,
                    });
                  }}
                  variant="outline"
                  className="border-zinc-700 bg-transparent hover:bg-zinc-900 rounded-sm h-8 mono text-[10px] uppercase tracking-widest gap-1.5 disabled:opacity-50"
                >
                  <PaperPlaneTilt size={12} weight="bold" /> Play both
                </Button>
                {/* Iter 64 — Reverse Chime Play. Fires ascending flip
                    chime first then descending urgent chime ~600ms
                    apart so admins can hear the reversed order for
                    comparison. Shares mute/volume with the primary
                    Play both button. */}
                <Button
                  type="button"
                  data-testid="chime-play-both-reverse-btn"
                  disabled={chimeMuted}
                  onClick={() => {
                    stopChimeLoop();
                    playBothChimesReverse();
                    if (chimeLoop) {
                      startChimeLoop(playBothChimesReverse, chimeLoopIntervalMs, "reverse");
                    }
                    toast.success(chimeLoop ? "Looping both chimes (reverse)" : "Playing both chimes (reverse)", {
                      description: chimeLoop
                        ? `↑ flip · ↓ urgent — repeating every ${(chimeLoopIntervalMs / 1000).toFixed(1)}s. Click Stop to end.`
                        : "↑ flip · ↓ urgent — ascending fires first, descending ~600ms later.",
                      duration: chimeLoop ? 3000 : 4000,
                    });
                  }}
                  variant="outline"
                  className="border-zinc-700 bg-transparent hover:bg-zinc-900 rounded-sm h-8 mono text-[10px] uppercase tracking-widest gap-1.5 disabled:opacity-50"
                >
                  <CaretUp size={12} weight="bold" /> Play both (reverse)
                </Button>
                {/* Iter 65 — Chime Loop Toggle. Small checkbox that
                    switches the two Play both buttons from one-shot
                    to repeating. Toggling off while a loop is running
                    also stops it. A Stop button appears while a loop
                    is active as an explicit escape hatch. */}
                <label
                  data-testid="chime-loop-toggle-label"
                  className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-zinc-400 select-none cursor-pointer"
                >
                  <input
                    type="checkbox"
                    data-testid="chime-loop-toggle"
                    checked={chimeLoop}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setChimeLoop(next);
                      if (!next) stopChimeLoop();
                    }}
                    className="accent-amber-500 w-3 h-3 cursor-pointer"
                  />
                  Loop
                </label>
                {/* Iter 66 — Chime Loop Interval Slider. Lets admins
                    tune the loop pacing to match different real-world
                    event rhythms. Persisted per-device. If a loop is
                    currently running, restarts it with the new
                    interval so tuning is immediate. */}
                <div className="inline-flex items-center gap-1.5 mono text-[9px] uppercase tracking-widest text-zinc-500 min-w-[140px]">
                  <span data-testid="chime-loop-interval-value" className="tabular-nums text-zinc-300 shrink-0 w-8">
                    {(chimeLoopIntervalMs / 1000).toFixed(1)}s
                  </span>
                  <Slider
                    data-testid="chime-loop-interval-slider"
                    min={500}
                    max={3000}
                    step={100}
                    value={[chimeLoopIntervalMs]}
                    onValueChange={(v) => {
                      const n = Array.isArray(v) ? v[0] : v;
                      applyChimeLoopInterval(n);
                    }}
                    className="w-24"
                  />
                </div>
                {/* Iter 67 — Loop Interval Presets. Three preset buttons
                    so admins can jump to common tempos in one click.
                    Reuses applyChimeLoopInterval so the slider value,
                    localStorage persistence, and running-loop restart
                    stay in one code path. */}
                <div
                  className="inline-flex items-center gap-1"
                  data-testid="chime-loop-presets"
                  data-focus-pulse={chimeFocusPulse ? "true" : "false"}
                  data-focus-pulse-fallback={chimeFocusPulseFallback ? "true" : "false"}
                >
                  {[500, 1000, 2000].map((ms) => {
                    const label = ms === 500 ? "0.5s" : `${Math.round(ms / 1000)}s`;
                    const isActive = chimeLoopIntervalMs === ms;
                    return (
                      <button
                        key={ms}
                        type="button"
                        data-testid={`loop-preset-${ms}`}
                        data-active={isActive ? "true" : "false"}
                        onClick={() => applyChimeLoopInterval(ms)}
                        className={
                          "mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border transition-colors tabular-nums " +
                          (isActive
                            ? "border-amber-500/60 bg-amber-500/15 text-amber-200"
                            : "border-zinc-700 bg-transparent text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200")
                        }
                        title={`Set loop tempo to ${label} · press ${ms === 500 ? "1" : ms === 1000 ? "2" : "3"} when the chime block has focus`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                {/* Iter 68 — Loop Presets Keyboard hint. Renders a small
                    reminder that keys 1/2/3 map to the 3 presets when
                    the chime block has focus. */}
                <span
                  data-testid="chime-loop-keys-hint"
                  className="mono text-[8px] uppercase tracking-widest text-zinc-600 select-none"
                  title="Focus this block (tab or click) and press 1 / 2 / 3 to jump between presets"
                >
                  1·2·3
                </span>
                {chimeLoopActive && (
                  <Button
                    type="button"
                    data-testid="chime-loop-stop-btn"
                    onClick={() => {
                      stopChimeLoop();
                      toast("Loop stopped", { duration: 2000 });
                    }}
                    variant="outline"
                    className="border-red-500/50 bg-red-500/5 hover:bg-red-500/15 text-red-300 rounded-sm h-8 mono text-[10px] uppercase tracking-widest gap-1.5"
                  >
                    Stop
                  </Button>
                )}
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Button
              onClick={saveSettings} disabled={savingSettings}
              data-testid="settings-save-btn"
              className="bg-[#00C805] hover:bg-[#00b004] text-black rounded-sm h-10 mono text-[10px] uppercase tracking-widest gap-1.5"
            >
              <FloppyDisk size={12} weight="bold" /> {savingSettings ? "Saving…" : "Save settings"}
            </Button>
            <Button
              onClick={clearSettings} disabled={savingSettings}
              variant="outline"
              data-testid="settings-reset-btn"
              className="border-zinc-700 bg-transparent hover:bg-zinc-900 rounded-sm h-10 mono text-[10px] uppercase tracking-widest"
            >
              Reset to defaults
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Weekly digest preview"
          subtitle="Delivered every Monday 09:00 UTC to accounts with alert-enabled bots"
          tone={digestTone === "urgent" ? "urgent" : "default"}
          action={
            <div className="flex items-center gap-2">
              {/* Iter 50 — when the countdown flips urgent (< 6h), promote
                  the Send now button to the leading slot AND swap its style
                  to red so users can fire the digest one tap early. Kept
                  in a separate branch so the normal-state layout stays
                  unchanged. */}
              {digestTone === "urgent" && data.email_configured && (
                <Button
                  onClick={sendDigest} disabled={sendingDigest}
                  data-testid="send-digest-btn-urgent"
                  className="bg-red-500 hover:bg-red-600 text-white rounded-sm h-8 mono text-[10px] uppercase tracking-widest gap-1.5 animate-pulse"
                >
                  <PaperPlaneTilt size={12} weight="bold" /> {sendingDigest ? "Sending…" : "Send now"}
                </Button>
              )}
              <NextDigestHint onToneChange={setDigestTone} />
              <Button
                onClick={() => setRecapOpen(true)}
                data-testid="preview-recap-btn"
                variant="outline"
                className="border-zinc-700 rounded-sm bg-transparent h-8 mono text-[10px] uppercase tracking-widest gap-1.5 text-white hover:bg-zinc-800"
              >
                <Eye size={12} /> Preview
              </Button>
              {digestTone !== "urgent" && data.email_configured && (
                <Button
                  onClick={sendDigest} disabled={sendingDigest}
                  data-testid="send-digest-btn"
                  className="bg-white text-black hover:bg-zinc-200 rounded-sm h-8 mono text-[10px] uppercase tracking-widest gap-1.5"
                >
                  <PaperPlaneTilt size={12} weight="bold" /> {sendingDigest ? "Sending…" : "Send now"}
                </Button>
              )}
            </div>
          }
        />
        {digest ? (
          <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="digest-preview">
            <DigestStat label="Week" value={`${digest.week_start.slice(5)} → ${digest.week_end.slice(5)}`} icon={CalendarBlank} />
            <DigestStat label="Bot trades" value={digest.num_trades} />
            <DigestStat
              label="Realized P/L"
              value={fmtMoney(digest.total_realized_pl, { showSign: true })}
              tone={digest.total_realized_pl >= 0 ? "profit" : "loss"}
            />
            <DigestStat
              label="Win rate"
              value={digest.num_trades > 0 ? `${digest.win_rate.toFixed(1)}%` : "—"}
              tone={digest.win_rate >= 50 ? "profit" : "loss"}
            />
            <DigestStat
              label="Top bot"
              value={digest.top_bot ? `${digest.top_bot.symbol} ${fmtMoney(digest.top_bot.pl, { showSign: true })}` : "—"}
              tone="profit"
              wide
            />
            <DigestStat
              label="Worst bot"
              value={digest.worst_bot ? `${digest.worst_bot.symbol} ${fmtMoney(digest.worst_bot.pl, { showSign: true })}` : "—"}
              tone="loss"
              wide
            />
            <DigestStat
              label="Biggest win"
              value={digest.biggest_win ? fmtMoney(digest.biggest_win, { showSign: true }) : "—"}
              tone="profit"
            />
            <DigestStat
              label="Biggest loss"
              value={digest.biggest_loss ? fmtMoney(digest.biggest_loss, { showSign: true }) : "—"}
              tone="loss"
            />
          </div>
        ) : (
          <div className="px-5 py-6 mono text-xs text-zinc-600">Loading digest…</div>
        )}
      </Card>

      <Card data-testid="notifications-table-card">
        <CardHeader
          title={`${(flipperFilter && adminNotifications ? adminNotifications : data.notifications).length} delivered / attempted${flipperFilter ? ` · viewing ${flipperFilter.email_masked}` : ""}`}
          action={
            <div className="flex items-center gap-2">
              {/* Iter 52 — active flipper filter chip. Removing it restores
                  the admin's own notifications feed.
                  Iter 54 — includes a Copy-Link button so admins can share
                  the deep-linked filtered view in Slack without hand-copying
                  the address bar. */}
              {flipperFilter && (
                <div className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    data-testid="flipper-copy-link-btn"
                    data-copied={linkCopied ? "true" : "false"}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(window.location.href);
                        setLinkCopied(true);
                        // Auto-reset after 2s so the button returns to its
                        // idle state ready for the next share.
                        setTimeout(() => setLinkCopied(false), 2000);
                        toast.success("Link copied", {
                          description: "Share the filtered view with your team.",
                          duration: 3000,
                        });
                      } catch {
                        toast.error("Copy failed");
                      }
                    }}
                    className={
                      "mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border transition-all duration-200 relative overflow-hidden " +
                      (linkCopied
                        ? "border-[#00C805]/70 bg-[#00C805]/10 text-[#00C805]"
                        : "border-zinc-700 bg-transparent hover:bg-zinc-800 text-zinc-300")
                    }
                    title="Copy shareable URL"
                  >
                    {/* Iter 57 — two absolutely-positioned inner spans
                        fade between idle/copied so the icon+text swap
                        feels intentional. Layout stabilised by a hidden
                        placeholder that reserves the wider of the two
                        widths. */}
                    <span className="invisible inline-flex items-center gap-1.5" aria-hidden="true">
                      <LinkIcon size={12} strokeWidth={2} /> Copied
                    </span>
                    <span
                      data-testid="flipper-copy-link-idle"
                      className={
                        "absolute inset-0 flex items-center justify-center gap-1.5 transition-opacity duration-200 " +
                        (linkCopied ? "opacity-0" : "opacity-100")
                      }
                    >
                      <LinkIcon size={12} strokeWidth={2} /> Copy
                    </span>
                    <span
                      data-testid="flipper-copy-link-copied"
                      className={
                        "absolute inset-0 flex items-center justify-center gap-1.5 transition-opacity duration-200 " +
                        (linkCopied ? "opacity-100" : "opacity-0")
                      }
                    >
                      <CheckIcon size={12} strokeWidth={2.5} /> Copied
                    </span>
                  </button>
                  <button
                    type="button"
                    data-testid="flipper-filter-chip"
                    onClick={clearFlipperFilter}
                    className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border border-amber-500/60 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 gap-1.5 inline-flex items-center"
                    title="Clear flipper filter"
                  >
                    {flipperFilter.email_masked}
                    <span className="text-amber-500">✕</span>
                  </button>
                </div>
              )}
              {data.failed_count > 0 && !flipperFilter && (
                <Button
                  onClick={() => setPurgeOpen(true)} disabled={purging}
                  data-testid="purge-failed-btn"
                  variant="outline"
                  className="border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-sm h-8 mono text-[10px] uppercase tracking-widest gap-1.5 bg-transparent"
                >
                  <Broom size={12} weight="bold" /> Purge {data.failed_count} failed
                </Button>
              )}
              <div className="flex gap-1">
                {[
                  { k: "all", label: "All" },
                  { k: "trades", label: "Trades" },
                  { k: "digest", label: "Digest" },
                  { k: "mood", label: "Mood shifts" },
                ].map(f => (
                  <button
                    key={f.k}
                    data-testid={`notif-filter-${f.k}`}
                    onClick={() => setNotifFilter(f.k)}
                    className={`px-2.5 py-1 text-[10px] mono uppercase tracking-widest rounded-sm border ${
                      notifFilter === f.k
                        ? "border-white bg-white text-black"
                        : "border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-600"
                    }`}
                  >{f.label}</button>
                ))}
              </div>
            </div>
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] mono uppercase tracking-widest text-zinc-500">
              <tr className="border-b border-zinc-800">
                <th className="text-left px-5 py-2.5">Time</th>
                <th className="text-left px-3 py-2.5">Type</th>
                <th className="text-left px-3 py-2.5">Symbol</th>
                <th className="text-left px-3 py-2.5">Side</th>
                <th className="text-left px-3 py-2.5">Subject</th>
                <th className="text-left px-5 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="mono">
              {(flipperFilter && adminNotifications ? adminNotifications : data.notifications)
                .filter(n => {
                  const isDigest = n.kind === "weekly_digest";
                  const isMood = n.kind === "sentiment_alert" || n.kind === "watchlist_alert";
                  const isDeepPause = n.kind === "deep_pause_streak";
                  if (notifFilter === "trades") return !isDigest && !isMood && !isDeepPause;
                  if (notifFilter === "digest") return isDigest;
                  if (notifFilter === "mood") return isMood;
                  return true;
                })
                .map(n => {
                  const isDigest = n.kind === "weekly_digest";
                  const isMood = n.kind === "sentiment_alert" || n.kind === "watchlist_alert";
                  const isDeepPause = n.kind === "deep_pause_streak";
                  const isOpen = !!expanded[n.id];
                  return (
                    <React.Fragment key={n.id}>
                      <tr
                        className={`border-b border-zinc-800/50 ${isDigest ? "bg-[#007AFF]/5 cursor-pointer" : ""} hover:bg-zinc-800/30`}
                        data-testid={`notification-${n.id}`}
                        onClick={() => isDigest && toggleExpand(n.id)}
                      >
                        <td className="px-5 py-3 text-zinc-400 text-xs">{new Date(n.at).toLocaleString()}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5">
                            {isDigest && (
                              <span data-testid={`digest-caret-${n.id}`}>
                                {isOpen ? <CaretUp size={14} className="text-[#007AFF]" /> : <CaretDown size={14} className="text-[#007AFF]" />}
                              </span>
                            )}
                            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${
                              isDigest
                                ? "border-[#007AFF] text-[#007AFF] bg-[#007AFF]/10"
                                : isDeepPause
                                ? "border-red-500/60 text-red-400 bg-red-500/10"
                                : isMood
                                ? "border-amber-500/40 text-amber-400 bg-amber-500/10"
                                : "border-zinc-700 text-zinc-400"
                            }`}>
                              {isDigest ? "Digest" : isDeepPause ? "Retire" : isMood ? "Mood" : "Trade"}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-3 font-semibold">
                          {isDigest ? (
                            n.summary?.top_bot ? (
                              <span className="text-xs">
                                <span className="profit">↑{n.summary.top_bot.symbol}</span>
                                {n.summary?.worst_bot && <> · <span className="loss">↓{n.summary.worst_bot.symbol}</span></>}
                              </span>
                            ) : <span className="text-zinc-500">—</span>
                          ) : isDeepPause ? (n.meta?.symbol || n.symbol || "—") : n.symbol}
                        </td>
                        <td className={`px-3 py-3 uppercase font-bold ${
                          isDigest
                            ? (n.summary && n.summary.num_trades > 0
                                ? (n.summary.total_realized_pl >= 0 ? "profit" : "loss")
                                : "text-zinc-500")
                            : (n.side === "buy" ? "profit" : "loss")
                        }`}>
                          {isDigest ? (
                            n.summary && n.summary.num_trades > 0 ? (
                              fmtMoney(n.summary.total_realized_pl, { showSign: true })
                            ) : "—"
                          ) : n.side}
                        </td>
                        <td className="px-3 py-3 text-xs text-zinc-300 truncate max-w-[320px]">
                          {isDeepPause ? (
                            <div className="flex items-center gap-2">
                              <span className="truncate">{n.message || n.subject}</span>
                              {n.meta?.bot_id && !archivedBotIds.has(n.meta.bot_id) && (
                                <button
                                  type="button"
                                  data-testid={`archive-bot-${n.meta.bot_id}`}
                                  disabled={archivingBotId === n.meta.bot_id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    archiveBot(n.meta.bot_id, n.meta.symbol);
                                  }}
                                  className="shrink-0 mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-sm border border-red-500/50 text-red-400 hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-wait transition-colors"
                                >
                                  {archivingBotId === n.meta.bot_id ? "Archiving…" : "Archive bot"}
                                </button>
                              )}
                              {n.meta?.bot_id && archivedBotIds.has(n.meta.bot_id) && (
                                <span
                                  data-testid={`archived-badge-${n.meta.bot_id}`}
                                  className="shrink-0 mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-sm border border-zinc-700 text-zinc-500"
                                >
                                  Archived
                                </span>
                              )}
                            </div>
                          ) : n.subject}
                        </td>
                        <td className="px-5 py-3">
                          {n.delivered ? (
                            <span className="text-[10px] uppercase tracking-widest text-[#00C805] border border-[#00C805]/30 rounded-sm px-2 py-0.5">Delivered</span>
                          ) : (
                            <span title={n.error || ""} className="text-[10px] uppercase tracking-widest text-[#FF3B30] border border-[#FF3B30]/30 rounded-sm px-2 py-0.5">Failed</span>
                          )}
                        </td>
                      </tr>
                      {isDigest && isOpen && n.summary && (
                        <tr className="bg-[#007AFF]/[0.03] border-b border-zinc-800/50" data-testid={`digest-detail-${n.id}`}>
                          <td colSpan={6} className="px-5 py-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <DigestStat label="Week"
                                value={n.summary.week_start && n.summary.week_end
                                  ? `${n.summary.week_start.slice(5)} → ${n.summary.week_end.slice(5)}`
                                  : "—"}
                                icon={CalendarBlank} />
                              <DigestStat label="Bot trades" value={n.summary.num_trades ?? 0} />
                              <DigestStat label="Win rate"
                                value={n.summary.num_trades > 0 ? `${(n.summary.win_rate ?? 0).toFixed(1)}%` : "—"}
                                tone={(n.summary.win_rate ?? 0) >= 50 ? "profit" : "loss"} />
                              <DigestStat label="Portfolio"
                                value={n.summary.portfolio_value != null ? fmtMoney(n.summary.portfolio_value) : "—"} />
                              <DigestStat label="Top bot"
                                value={n.summary.top_bot
                                  ? `${n.summary.top_bot.symbol} ${fmtMoney(n.summary.top_bot.pl, { showSign: true })}`
                                  : "—"} tone="profit" wide />
                              <DigestStat label="Worst bot"
                                value={n.summary.worst_bot
                                  ? `${n.summary.worst_bot.symbol} ${fmtMoney(n.summary.worst_bot.pl, { showSign: true })}`
                                  : "—"} tone="loss" wide />
                              <DigestStat label="Biggest win"
                                value={n.summary.biggest_win ? fmtMoney(n.summary.biggest_win, { showSign: true }) : "—"} tone="profit" />
                              <DigestStat label="Biggest loss"
                                value={n.summary.biggest_loss ? fmtMoney(n.summary.biggest_loss, { showSign: true }) : "—"} tone="loss" />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              {(flipperFilter && adminNotifications ? adminNotifications : data.notifications).length === 0 && (
                <tr><td colSpan={6} className="px-5 py-16 text-center mono text-xs text-zinc-600">
                  {flipperFilter
                    ? `No notifications for ${flipperFilter.email_masked}.`
                    : "No notifications yet. Enable email alerts on a bot to start receiving trade updates."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <AlertDialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="heading text-2xl tracking-tighter">
              Purge failed notifications?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400 text-sm">
              This will permanently delete <span className="text-white mono">{data.failed_count}</span> failed
              notification{data.failed_count === 1 ? "" : "s"} from your history.
              Delivered notifications will not be affected. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="purge-cancel-btn"
              disabled={purging}
              className="border-zinc-700 bg-transparent hover:bg-zinc-800 text-white rounded-sm mono text-[10px] uppercase tracking-widest disabled:opacity-50"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="purge-confirm-btn"
              onClick={purgeFailed}
              disabled={purging}
              className="bg-[#FF3B30] hover:bg-[#e63329] text-white rounded-sm mono text-[10px] uppercase tracking-widest gap-1.5"
            >
              <Broom size={12} weight="bold" /> {purging ? "Purging…" : "Delete forever"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {isAdmin && <TrendSortAnalyticsCard onFilterByUser={applyFlipperFilter} />}
      {isAdmin && <UndoEventsAnalyticsCard />}
      {isAdmin && <RateLimitDashboardCard />}
      <WeeklyRecapPreviewModal open={recapOpen} onOpenChange={setRecapOpen} />
    </div>
  );
}

function relativeTime(ts) {
  const seconds = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function DigestStat({ label, value, tone = "default", wide = false, icon: Icon }) {
  const tc = tone === "profit" ? "profit" : tone === "loss" ? "loss" : "text-white";
  return (
    <div className={`border border-zinc-800 rounded-sm p-3 bg-zinc-950 ${wide ? "col-span-2" : ""}`}>
      <div className="mono text-[9px] text-zinc-500 uppercase tracking-widest flex items-center gap-1.5">
        {Icon && <Icon size={10} />}
        {label}
      </div>
      <div className={`mono text-base font-bold mt-1.5 truncate ${tc}`}>{value}</div>
    </div>
  );
}

/**
 * Inline "Next digest · in 4d 12h" live countdown sourced from
 * ``/api/scheduler/status``. Renders nothing until the fetch resolves so
 * we don't flash an empty placeholder next to the Send now button.
 *
 * Countdown re-renders every 30 seconds so users see the send time
 * approach in real time without needing a page refresh — matches the
 * ambient rhythm of the rest of the dashboard.
 */
function NextDigestHint({ onToneChange }) {
  const [next, setNext] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let stop = false;
    api.get("/scheduler/status")
      .then((r) => {
        if (stop) return;
        const job = (r.data?.jobs || []).find((j) => j.id === "weekly_digest");
        setNext(job?.next_run_at || null);
      })
      .catch(() => {});
    return () => { stop = true; };
  }, []);

  // Tick every 30s — precise enough that "in 4d 12h" swaps to "in 4d 11h"
  // when the hour rolls over, cheap enough to leave running forever.
  useEffect(() => {
    if (!next) return;
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [next]);

  // Derive diff & tone before the guards so we can call the tone-broadcast
  // effect unconditionally (React hook rules). ``d`` may be null when
  // next is missing / malformed — treat that as "normal" so the parent
  // header stays neutral until data arrives.
  const d = next ? new Date(next) : null;
  const validDate = d && Number.isFinite(d.getTime());
  const diff = validDate ? d.getTime() - now : Number.POSITIVE_INFINITY;
  const HOUR_MS = 60 * 60 * 1000;
  const isUrgent = validDate && diff <= 6 * HOUR_MS;
  const isWarning = validDate && !isUrgent && diff <= 24 * HOUR_MS;
  const toneKey = isUrgent ? "urgent" : isWarning ? "warning" : "normal";

  // Iter 49 — bubble the tone up so the parent Card can tint its header.
  // Called unconditionally (must precede any early return) so the hook
  // order stays stable across renders.
  useEffect(() => {
    onToneChange?.(toneKey);
  }, [toneKey, onToneChange]);

  if (!next) return null;
  if (!validDate) return null;

  const utcLabel = d.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
    hour12: true,
  });

  const countdown = formatCountdown(diff);
  // Iter 47 — color-ramp the countdown so users notice the impending send
  // window at a glance. Red < 6h (urgent, digest fires soon), amber
  // 6-24h (heads-up), default white otherwise.
  const countdownTone = isUrgent ? "text-red-400" : isWarning ? "text-amber-400" : "text-white";
  // Iter 48 — heartbeat pulse when tone flips to urgent so users glance at
  // it during the final 6h send window. `animate-pulse` is Tailwind's
  // built-in gentle opacity fade — subtle enough not to be annoying.
  const pulseClass = isUrgent ? " animate-pulse" : "";

  return (
    <span
      data-testid="next-digest-hint"
      title={`Scheduled fire time (UTC): ${utcLabel} · ${next}`}
      className="mono text-[10px] uppercase tracking-widest text-zinc-500 border border-zinc-800 rounded-sm px-2 py-1 select-none"
    >
      Next{" "}
      <span
        data-testid="next-digest-countdown"
        data-tone={toneKey}
        className={countdownTone + pulseClass}
      >{countdown}</span>
      {" · "}
      <span className="text-zinc-600">{utcLabel} UTC</span>
    </span>
  );
}

/**
 * Format a millisecond offset into a compact "in 4d 12h" / "in 3h 22m" /
 * "in 45s" string. Past-due offsets flip to "sending now" — the scheduler
 * fires within a minute so a countdown that ran to zero is functionally
 * mid-run.
 */
function formatCountdown(ms) {
  if (ms <= 0) return "sending now";
  const totalMinutes = Math.floor(ms / 60_000);
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;
  if (days > 0) return `in ${days}d ${hours}h`;
  if (hours > 0) return `in ${hours}h ${minutes}m`;
  if (minutes > 0) return `in ${minutes}m`;
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `in ${seconds}s`;
}

/**
 * Iter 50 — Admin-only card surfacing `GET /api/admin/analytics/trend-sort`
 * so the team can glance at flip-adoption without curling the API. Silently
 * renders nothing if the fetch 403s (belt-and-braces since the parent
 * already gates on `role==='admin'`) or errors.
 */
function TrendSortAnalyticsCard({ onFilterByUser }) {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/admin/analytics/trend-sort")
      .then((r) => setStats(r.data))
      .catch(() => setErr(true));
  }, []);

  if (err) return null;
  const flipPct = stats && stats.total_users > 0
    ? Math.round((stats.users_with_flips / stats.total_users) * 100)
    : 0;

  return (
    <Card data-testid="trend-sort-analytics-card">
      <CardHeader
        title="Admin · Trend-sort adoption"
        subtitle="How many users are actually flipping the trend-sort arrow. Persistence pays off when flippers > 0."
      />
      {stats ? (
        <>
          <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="trend-sort-analytics-body">
            <DigestStat label="Total users" value={stats.total_users} />
            <DigestStat
              label="Have flipped"
              value={`${stats.users_with_flips} · ${flipPct}%`}
              tone={stats.users_with_flips > 0 ? "profit" : "default"}
            />
            <DigestStat label="Total flips" value={stats.total_flips} />
            <DigestStat label="Avg flips / flipper" value={stats.avg_flips_per_flipper} />
            <DigestStat
              label="Currently desc (▼)"
              value={stats.desc_count}
              tone="profit"
            />
            <DigestStat
              label="Currently asc (▲)"
              value={stats.asc_count}
              tone="loss"
            />
          </div>
          {/* Iter 51 — recent flippers mini-feed. Only rendered when there's
              at least one flipper; keeps the empty state clean. */}
          {Array.isArray(stats.recent_flippers) && stats.recent_flippers.length > 0 && (
            <div className="px-5 pb-5" data-testid="trend-sort-recent-flippers">
              <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest mb-2">
                Recent flippers · last 5
              </div>
              <div className="border border-zinc-800 rounded-sm bg-zinc-950 divide-y divide-zinc-800/60">
                {stats.recent_flippers.map((f, i) => (
                  <button
                    key={`flipper-${i}`}
                    type="button"
                    data-testid={`recent-flipper-${i}`}
                    onClick={() => onFilterByUser?.(f)}
                    className="w-full flex items-center justify-between px-3 py-2 mono text-xs text-left hover:bg-amber-500/5 hover:cursor-pointer transition-colors"
                    title={`Filter notifications by ${f.email_masked}`}
                  >
                    <span className="text-zinc-300 group-hover:text-amber-300">{f.email_masked}</span>
                    <div className="flex items-center gap-3 text-[10px] text-zinc-500 uppercase tracking-widest">
                      <span data-testid={`recent-flipper-${i}-count`}>
                        {f.flip_count} flip{f.flip_count === 1 ? "" : "s"}
                      </span>
                      <span
                        data-testid={`recent-flipper-${i}-dir`}
                        className={f.current_direction === "desc" ? "profit" : "loss"}
                      >
                        {f.current_direction === "desc" ? "▼ desc" : "▲ asc"}
                      </span>
                      {f.last_flip_at && (
                        <span className="text-zinc-600" title={f.last_flip_at}>
                          {new Date(f.last_flip_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="px-5 py-6 mono text-xs text-zinc-600">Loading analytics…</div>
      )}
    </Card>
  );
}




/**
 * Iter 54 — Undo Success Analytics. Admin-only card that surfaces the
 * shown/clicked/expired counts per Undo-toast kind so we can measure
 * whether the 8s window (and the auto-extend) is the right budget.
 */
function UndoEventsAnalyticsCard() {
  // Iter 59 — use search params inside the card so range changes deep-link
  // to the URL. Independent from the parent NotificationsPage's search
  // params usage; both instances resolve to the same react-router state.
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  // Iter 56 — extend-threshold slider state. Fetched on mount; PUT'd on
  // change so all users' UndoCountdownChip instances pick it up on next
  // fetch (module-level cache primed via setExtendThreshold).
  const [threshold, setThreshold] = useState(2);
  // Iter 57 — CSV preset range (days). 7 | 30 | 0 (=all-time up to 365d).
  // Also drives the analytics fetch so the on-screen sparklines match the
  // selection.
  // Iter 58 — persist the choice so admins don't have to re-select on
  // every visit. Keyed to keep the pref localStorage-only (no server
  // round-trip needed — this is a per-device UI preference).
  // Iter 59 — URL param ``?range=<n>`` takes precedence over localStorage
  // so admins can deep-link a filtered view of the analytics.
  const [rangeDays, setRangeDays] = useState(() => {
    try {
      const urlRange = parseInt(new URLSearchParams(window.location.search).get("range"), 10);
      if ([7, 30, 0].includes(urlRange)) return urlRange;
      const raw = localStorage.getItem("stratos.undo-analytics.range");
      const n = parseInt(raw, 10);
      if ([7, 30, 0].includes(n)) return n;
    } catch {}
    return 7;
  });
  // Iter 60 — URL Share Button state; mirrors the flipper-copy-link
  // 2-second "Copied" affordance for a consistent feel.
  const [analyticsLinkCopied, setAnalyticsLinkCopied] = useState(false);
  // Iter 61 — CSV Preview Pin. Locks the hover modal open so admins can
  // compare the top-3 rows to the sparklines without losing hover state
  // when the cursor moves off the trigger chip.
  const [csvPreviewPinned, setCsvPreviewPinned] = useState(false);
  // Iter 67 — Seed Variance Custom Values. Comma-list of CTR values
  // used by the Force-CTR Test Seeder. Default matches the historical
  // set so existing screenshots still reproduce.
  // Iter 68 — Pro Hint Reset. Mirrors the localStorage flag so the
  // reset button can appear/disappear reactively without a full page
  // reload. Kept as an admin-only dev toggle so demo recordings can
  // re-trigger the delight moment.
  const [proHintSeen, setProHintSeen] = useState(() => {
    try { return localStorage.getItem("stratos.csv-cancel-pro-hint-seen") === "1"; } catch { return false; }
  });
  // Iter 77 — seed-input state (seedValuesInput, debouncedSeedValuesInput,
  // debounceElapsedMs, seedRandomRange, legendCounts, legendChanged +
  // their effects) moved into SeedInputPanel component.
  // Iter 62 — Share Tooltip Copy Row. Tracks which param key was just
  // copied so we can flash the row green + swap icon briefly. null =
  // no row currently in "just-copied" state.
  const [copiedParamKey, setCopiedParamKey] = useState(null);
  // Iter 62 — Auto-Pin On Long Hover. Starts a 2s timer on mouseenter of
  // the CSV row-count chip; when the timer fires we set pinned=true and
  // flag it as auto (so a hint chip renders inside the modal). Explicit
  // pin toggle clears the auto flag. moving the cursor off cancels
  // the timer without pinning.
  const [csvAutoPinned, setCsvAutoPinned] = useState(false);
  const csvAutoPinTimerRef = React.useRef(null);
  // Iter 63 — Cancel Auto-Pin Hint. True while the 2s auto-pin timer is
  // active so the row-count chip can render a countdown progress ring;
  // flipped false when the timer fires, cancels, or the modal is
  // manually pinned/unpinned.
  const [csvAutoPinPending, setCsvAutoPinPending] = useState(false);
  // Iter 64 — Cancel Hint Countdown. Ticks 2 → 1 → 0 alongside the 2s
  // auto-pin timer so admins see the exact seconds remaining. Kept
  // separate from ``csvAutoPinPending`` so the state transitions
  // stay readable (interval + timeout are cleared together on
  // cancel/fire).
  const [csvAutoPinCountdown, setCsvAutoPinCountdown] = useState(0);
  const csvAutoPinCountdownRef = React.useRef(null);
  // Iter 66 — Countdown Halt Flash. Renders a brief red "canceled" chip
  // (~400ms) when a running auto-pin timer is cancelled by mouseleave
  // so admins get visible confirmation that they saved the pin from
  // firing. Cleared by its own timeout; a fresh hover replaces it.
  const [csvAutoPinHalt, setCsvAutoPinHalt] = useState(false);
  const csvAutoPinHaltTimerRef = React.useRef(null);
  // Iter 67 — Seed Variance Custom Values. Comma-list of CTR values
  // used by the Force-CTR Test Seeder. Default matches the historical
  // set so existing screenshots still reproduce.
  const cancelCsvAutoPinTimer = ({ userCancelled = false } = {}) => {
    const wasRunning = csvAutoPinTimerRef.current !== null;
    if (csvAutoPinTimerRef.current) {
      clearTimeout(csvAutoPinTimerRef.current);
      csvAutoPinTimerRef.current = null;
    }
    if (csvAutoPinCountdownRef.current) {
      clearInterval(csvAutoPinCountdownRef.current);
      csvAutoPinCountdownRef.current = null;
    }
    setCsvAutoPinPending(false);
    setCsvAutoPinCountdown(0);
    // Iter 66 — only flash when the user cancelled (mouseleave) while
    // the timer was actually running. Skips the flash on auto-fire
    // completion or programmatic cancels (manual pin toggle, unmount).
    if (userCancelled && wasRunning) {
      if (csvAutoPinHaltTimerRef.current) clearTimeout(csvAutoPinHaltTimerRef.current);
      setCsvAutoPinHalt(true);
      csvAutoPinHaltTimerRef.current = window.setTimeout(() => {
        setCsvAutoPinHalt(false);
        csvAutoPinHaltTimerRef.current = null;
      }, 400);
      // Iter 67 — Halt Flash Persistence Hint. Track cumulative cancels
      // in localStorage and, when the counter reaches exactly 3 for
      // the first time, drop a small delight toast so admins feel
      // rewarded for staying attentive. Only fires once via a paired
      // ``pro-hint-seen`` flag.
      try {
        const cur = parseInt(localStorage.getItem("stratos.csv-cancel-count") || "0", 10);
        const next = (Number.isFinite(cur) ? cur : 0) + 1;
        localStorage.setItem("stratos.csv-cancel-count", String(next));
        if (next === 3 && !localStorage.getItem("stratos.csv-cancel-pro-hint-seen")) {
          localStorage.setItem("stratos.csv-cancel-pro-hint-seen", "1");
          setProHintSeen(true);
          toast.success("You've saved the pin 3 times — you're a pro", {
            description: "The auto-pin hint won't sneak up on you again.",
            duration: 4000,
          });
        }
      } catch {}
    }
  };
  const startCsvAutoPinTimer = () => {
    if (csvPreviewPinned) return; // already pinned; nothing to do
    // Clear any lingering halt flash from the previous hover so the
    // new countdown reads clean.
    if (csvAutoPinHaltTimerRef.current) {
      clearTimeout(csvAutoPinHaltTimerRef.current);
      csvAutoPinHaltTimerRef.current = null;
    }
    setCsvAutoPinHalt(false);
    cancelCsvAutoPinTimer();
    setCsvAutoPinPending(true);
    setCsvAutoPinCountdown(2);
    csvAutoPinCountdownRef.current = window.setInterval(() => {
      setCsvAutoPinCountdown((n) => Math.max(0, n - 1));
    }, 1000);
    csvAutoPinTimerRef.current = window.setTimeout(() => {
      setCsvPreviewPinned(true);
      setCsvAutoPinned(true);
      setCsvAutoPinPending(false);
      setCsvAutoPinCountdown(0);
      if (csvAutoPinCountdownRef.current) {
        clearInterval(csvAutoPinCountdownRef.current);
        csvAutoPinCountdownRef.current = null;
      }
      csvAutoPinTimerRef.current = null;
    }, 2000);
  };
  useEffect(() => () => cancelCsvAutoPinTimer(), []);

  const copyParamRow = async (key, value) => {
    try {
      await navigator.clipboard.writeText(`${key}=${value}`);
      setCopiedParamKey(key);
      setTimeout(() => setCopiedParamKey((cur) => (cur === key ? null : cur)), 1500);
      toast.success(`Copied ${key}=${value}`, {
        description: "Ready to paste into Slack or a chat message.",
        duration: 3000,
      });
    } catch {
      toast.error("Copy failed");
    }
  };

  const copyAnalyticsLink = async () => {
    try {
      // Iter 60 — append a #undo-analytics anchor so recipients land
      // directly at this card even on a long-scroll page.
      const url = new URL(window.location.href);
      url.hash = "undo-analytics";
      await navigator.clipboard.writeText(url.toString());
      setAnalyticsLinkCopied(true);
      setTimeout(() => setAnalyticsLinkCopied(false), 2000);
      toast.success("Analytics link copied", {
        description: "Anchors directly to the Undo click-through card.",
        duration: 3000,
      });
    } catch {
      toast.error("Copy failed");
    }
  };
  // Iter 57 — threshold A/B history — recent snapshots of (threshold, ctr)
  // so admins can see which threshold produced the best CTR.
  const [history, setHistory] = useState(null);

  const fetchAnalytics = (d) => {
    api.get(`/admin/analytics/undo-events?days=${d}`)
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  };

  useEffect(() => {
    fetchAnalytics(rangeDays);
    api.get("/undo-extend-config")
      .then((r) => {
        const t = Number(r.data?.threshold_seconds);
        if (Number.isFinite(t)) {
          setThreshold(t);
          setExtendThreshold(t);
        }
      })
      .catch(() => {});
    api.get("/admin/analytics/threshold-history?limit=20")
      .then((r) => setHistory(r.data?.history || []))
      .catch(() => setHistory([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRangeChange = (v) => {
    const n = parseInt(v, 10);
    setRangeDays(n);
    // Iter 58 — persist to localStorage so the choice survives reloads.
    try { localStorage.setItem("stratos.undo-analytics.range", String(n)); } catch {}
    // Iter 59 — also mirror to URL so admins can share the deep-link.
    // Uses the shared react-router SearchParams so existing params like
    // ``?viewing=<uid>`` (from the flipper filter) are preserved.
    const next = new URLSearchParams(searchParams);
    next.set("range", String(n));
    setSearchParams(next, { replace: false });
    fetchAnalytics(n);
  };

  const onThresholdChange = (v) => {
    const n = Array.isArray(v) ? v[0] : v;
    setThreshold(n);
    setExtendThreshold(n);
    api.put("/admin/undo-extend-config", { threshold_seconds: n })
      .then(() => {
        // Iter 57 — refresh the A/B history after each write so the chart
        // reflects the new datapoint immediately.
        api.get("/admin/analytics/threshold-history?limit=20")
          .then((r) => setHistory(r.data?.history || []))
          .catch(() => {});
      })
      .catch(() => {});
  };

  const downloadCsv = () => {
    if (!data) return;
    // Iter 56 — CSV export. Emit one row per (kind, date) with all
    // counters so operators can pivot in a spreadsheet. Header first,
    // then a totals row for at-a-glance summary before the trend
    // breakdown.
    const rows = [
      ["kind", "date", "shown", "clicked", "extended", "expired", "ctr_pct"],
    ];
    (data.breakdown || []).forEach((b) => {
      // Totals row per kind
      rows.push([b.kind, "TOTAL", b.shown, b.clicked, b.extended ?? 0, b.expired, b.ctr_pct]);
      (b.daily || []).forEach((d) => {
        const dailyExpired = Math.max(0, d.shown - d.clicked);
        const dailyCtr = d.shown > 0 ? Math.round((d.clicked / d.shown) * 1000) / 10 : 0;
        rows.push([b.kind, d.date, d.shown, d.clicked, d.extended ?? 0, dailyExpired, dailyCtr]);
      });
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `undo-analytics-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    // Iter 58 — surface the exact row count so operators know what
    // they got without opening the file. rows.length includes the
    // header row — subtract 1 for the data-row count.
    const dataRows = Math.max(0, rows.length - 1);
    const rangeLabel = rangeDays === 0 ? "all-time" : `${rangeDays}d`;
    toast.success("CSV downloaded", {
      description: `${dataRows} row${dataRows === 1 ? "" : "s"} exported (${rangeLabel}).`,
      duration: 4000,
    });
  };

  if (err) return null;

  const KIND_LABELS = {
    mood_digest: "Mood digest toggle",
    save_settings: "Sender/recipient save",
    threshold: "Restore threshold",
  };

  return (
    <Card data-testid="undo-events-analytics-card" id="undo-analytics">
      <CardHeader
        title="Admin · Undo click-through"
        subtitle="Do users actually click Undo, or let the 8s window expire? Tune the budget when CTR is low. Auto-extend rate calibrates the ≤2s hover-late trigger."
        action={
          <div className="flex items-center gap-2">
            {/* Iter 60 — URL Share button. Copies the URL with a
                #undo-analytics anchor so recipients scroll straight
                to this card. Idle/copied cross-fade mirrors the
                flipper-copy-link chip style.
                Iter 61 — Share Button Tooltip. Hovering the button
                reveals a small floating list of exactly which query
                params + anchor the copied link will carry so admins
                know what teammates will see when they open it. */}
            <span className="relative group">
              <button
                type="button"
                data-testid="analytics-copy-link-btn"
                data-copied={analyticsLinkCopied ? "true" : "false"}
                onClick={copyAnalyticsLink}
                className={
                  "mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border transition-all duration-200 inline-flex items-center gap-1.5 " +
                  (analyticsLinkCopied
                    ? "border-[#00C805]/70 bg-[#00C805]/10 text-[#00C805]"
                    : "border-zinc-700 bg-transparent hover:bg-zinc-800 text-zinc-300")
                }
              >
                {analyticsLinkCopied
                  ? <><CheckIcon size={12} strokeWidth={2.5} /> Copied</>
                  : <><LinkIcon size={12} strokeWidth={2} /> Share</>
                }
              </button>
              {(() => {
                // Iter 61 — enumerate exactly what will be in the copied
                // URL so admins can preview it before pasting. Reads the
                // live URL so it stays truthful when other params are
                // set (e.g., ``?viewing=<uid>`` from the flipper chip).
                const params = new URLSearchParams(window.location.search);
                const known = [
                  { key: "range", label: "range", value: params.get("range") || String(rangeDays) },
                  { key: "viewing", label: "viewing", value: params.get("viewing") },
                ].filter((p) => p.value != null && p.value !== "");
                return (
                  <div
                    data-testid="analytics-share-tooltip"
                    className="absolute right-0 top-full mt-1 z-50 hidden group-hover:block bg-zinc-950 border border-zinc-700 rounded-sm shadow-lg p-2 w-[260px] text-left normal-case tracking-normal"
                  >
                    <div className="mono text-[9px] text-zinc-500 uppercase tracking-widest mb-1.5">
                      Link contents · click a row to copy
                    </div>
                    <ul className="mono text-[10px] text-zinc-300 space-y-1">
                      {known.length === 0 && (
                        <li className="text-zinc-500 italic" data-testid="share-tooltip-empty">
                          No query params — full-page defaults
                        </li>
                      )}
                      {known.map((p) => {
                        const isCopied = copiedParamKey === p.key;
                        return (
                          <li
                            key={p.key}
                            role="button"
                            tabIndex={0}
                            data-testid={`share-tooltip-param-${p.key}`}
                            data-copied={isCopied ? "true" : "false"}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              copyParamRow(p.label, p.value);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                e.stopPropagation();
                                copyParamRow(p.label, p.value);
                              }
                            }}
                            title={`Copy "${p.label}=${p.value}" to clipboard`}
                            className={
                              "flex justify-between gap-2 px-1 py-0.5 rounded-sm cursor-pointer select-none transition-colors " +
                              (isCopied
                                ? "bg-[#00C805]/15 text-[#00C805]"
                                : "hover:bg-zinc-900 text-zinc-300")
                            }
                          >
                            <span className={isCopied ? "text-[#00C805]/80" : "text-zinc-500"}>
                              {isCopied
                                ? <CheckIcon size={10} strokeWidth={2.5} className="inline mr-1" />
                                : null}
                              ?{p.label}=
                            </span>
                            <span className="tabular-nums truncate">{p.value}</span>
                          </li>
                        );
                      })}
                      <li
                        data-testid="share-tooltip-anchor"
                        className="flex justify-between gap-2 pt-1 border-t border-zinc-800 mt-1"
                      >
                        <span className="text-zinc-500">#anchor</span>
                        <span className="text-zinc-200">undo-analytics</span>
                      </li>
                      {/* Iter 63 — Copy Full URL Row. Bonus row at the
                          bottom of the tooltip that copies the entire
                          assembled URL (query params + anchor). Uses
                          the same click-to-copy + green flash pattern
                          as the individual param rows. */}
                      {(() => {
                        const fullUrl = (() => {
                          const u = new URL(window.location.href);
                          u.hash = "undo-analytics";
                          return u.toString();
                        })();
                        const isCopied = copiedParamKey === "__full_url__";
                        return (
                          <li
                            role="button"
                            tabIndex={0}
                            data-testid="share-tooltip-full-url"
                            data-copied={isCopied ? "true" : "false"}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              (async () => {
                                try {
                                  await navigator.clipboard.writeText(fullUrl);
                                  setCopiedParamKey("__full_url__");
                                  setTimeout(
                                    () => setCopiedParamKey((cur) => (cur === "__full_url__" ? null : cur)),
                                    1500,
                                  );
                                  toast.success("Full URL copied", {
                                    description: "Ready to paste — includes params and #undo-analytics anchor.",
                                    duration: 3000,
                                  });
                                } catch {
                                  toast.error("Copy failed");
                                }
                              })();
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.click();
                              }
                            }}
                            title="Copy the full URL to clipboard"
                            className={
                              "group/fullurl flex flex-col gap-0.5 px-1 py-0.5 rounded-sm cursor-pointer select-none transition-colors mt-1 " +
                              (isCopied
                                ? "bg-[#00C805]/15 text-[#00C805]"
                                : "hover:bg-zinc-900 text-zinc-300")
                            }
                          >
                            <span className="flex justify-between gap-2 items-center">
                              <span className={isCopied ? "text-[#00C805]/80" : "text-zinc-500 inline-flex items-center gap-1"}>
                                {isCopied
                                  ? <CheckIcon size={10} strokeWidth={2.5} className="inline mr-1" />
                                  : <LinkIcon size={10} strokeWidth={2} />}
                                full URL
                              </span>
                              <span className="tabular-nums truncate max-w-[180px] text-zinc-400">
                                {fullUrl.replace(/^https?:\/\//, "").slice(0, 32)}…
                              </span>
                            </span>
                            {/* Iter 64 — Full URL Preview Length. Reveal
                                a wrapped, full-width version of the
                                assembled URL on hover/focus so admins
                                can verify the entire link (protocol,
                                path, params, anchor) before copying.
                                Data-testid stays stable so tests can
                                assert on the text without needing
                                hover activation checks.
                                Iter 65 — Full URL One-Click Select.
                                Focus/click auto-selects the URL text
                                via getSelection + selectNodeContents
                                so admins on browsers that block async
                                clipboard writes can still Ctrl+C. */}
                            <span
                              data-testid="share-tooltip-full-url-preview"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                const sel = window.getSelection();
                                const range = document.createRange();
                                range.selectNodeContents(e.currentTarget);
                                sel.removeAllRanges();
                                sel.addRange(range);
                                // Iter 66 — URL Selection Toast. First-time
                                // hint so admins learn the Ctrl+C fallback.
                                // Persisted flag = only fires once per
                                // browser to avoid nagging repeat users.
                                try {
                                  if (!localStorage.getItem("stratos.url-select-hint-seen")) {
                                    localStorage.setItem("stratos.url-select-hint-seen", "1");
                                    toast("URL selected — press Ctrl+C", {
                                      description: "Works even when async clipboard writes are blocked.",
                                      duration: 3500,
                                    });
                                  }
                                } catch {}
                              }}
                              onFocus={(e) => {
                                const sel = window.getSelection();
                                const range = document.createRange();
                                range.selectNodeContents(e.currentTarget);
                                sel.removeAllRanges();
                                sel.addRange(range);
                              }}
                              className="hidden group-hover/fullurl:block group-focus-within/fullurl:block mono text-[9px] break-all leading-tight text-zinc-500 pt-1 border-t border-zinc-800/60 cursor-text select-all outline-none focus:text-zinc-300"
                            >
                              {fullUrl}
                            </span>
                          </li>
                        );
                      })()}
                    </ul>
                  </div>
                );
              })()}
            </span>
            {/* Iter 57 — CSV preset range dropdown. Refetches analytics
                immediately so the on-card sparklines and the CSV output
                share the same window. */}
            <Select value={String(rangeDays)} onValueChange={onRangeChange}>
              <SelectTrigger
                data-testid="undo-analytics-range-select"
                className="h-7 w-24 mono text-[10px] uppercase tracking-widest border-zinc-700 bg-transparent text-zinc-300"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7" data-testid="range-option-7d">7d</SelectItem>
                <SelectItem value="30" data-testid="range-option-30d">30d</SelectItem>
                <SelectItem value="0" data-testid="range-option-all">All-time</SelectItem>
              </SelectContent>
            </Select>
            <button
              type="button"
              data-testid="undo-analytics-csv-btn"
              onClick={downloadCsv}
              disabled={!data}
              className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border border-zinc-700 bg-transparent hover:bg-zinc-800 text-zinc-300 inline-flex items-center gap-1.5 disabled:opacity-40"
              title="Download breakdown for the selected range"
            >
              <Download size={12} strokeWidth={2} /> Export CSV
              {/* Iter 59 — anticipated row count preview. Sums per-kind
                  daily rows + a TOTAL row per kind (matches downloadCsv).
                  Zero-guards keep the chip hidden until data lands.
                  Iter 60 — hover the chip to see the top 3 CSV rows as
                  a floating preview so operators can sanity-check
                  before clicking Export. */}
              {data && (() => {
                const bd = data.breakdown || [];
                const total = bd.reduce((acc, b) => acc + 1 + ((b.daily || []).length), 0);
                // Build a preview of the first 3 data rows exactly as
                // downloadCsv would emit them (per-kind TOTAL then
                // daily rows).
                const previewRows = [];
                for (const b of bd) {
                  if (previewRows.length >= 3) break;
                  previewRows.push([b.kind, "TOTAL", b.shown, b.clicked, b.extended ?? 0, b.expired, b.ctr_pct]);
                  for (const d of (b.daily || [])) {
                    if (previewRows.length >= 3) break;
                    const dailyExpired = Math.max(0, d.shown - d.clicked);
                    const dailyCtr = d.shown > 0 ? Math.round((d.clicked / d.shown) * 1000) / 10 : 0;
                    previewRows.push([b.kind, d.date, d.shown, d.clicked, d.extended ?? 0, dailyExpired, dailyCtr]);
                  }
                }
                return (
                  <span
                    data-testid="undo-analytics-csv-row-count"
                    className="ml-1 text-zinc-500 tabular-nums relative group"
                    onMouseEnter={startCsvAutoPinTimer}
                    onMouseLeave={() => cancelCsvAutoPinTimer({ userCancelled: true })}
                  >
                    ({total})
                    {/* Iter 63 — Cancel Auto-Pin Hint. 2s animated ring
                        that draws around the trigger chip while the
                        auto-pin timer is running. Uses inline SVG so
                        we don't need a global keyframes definition:
                        stroke-dashoffset animates from full to 0.
                        Iter 64 — Cancel Hint Countdown. Small overlay
                        number ticks 2 → 1 → 0 while the timer runs so
                        admins see the exact seconds remaining and can
                        move off to cancel before it pins. */}
                    {csvAutoPinPending && (
                      <>
                        <svg
                          data-testid="csv-auto-pin-progress-ring"
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          viewBox="0 0 100 24"
                          preserveAspectRatio="none"
                          aria-hidden="true"
                        >
                          <rect
                            x="1"
                            y="1"
                            width="98"
                            height="22"
                            rx="3"
                            ry="3"
                            fill="none"
                            stroke="#FBBF24"
                            strokeWidth="1"
                            strokeDasharray="240"
                            strokeDashoffset="240"
                            style={{
                              animation: "csv-auto-pin-ring 2s linear forwards",
                            }}
                          />
                        </svg>
                        <span
                          data-testid="csv-auto-pin-countdown"
                          data-remaining={csvAutoPinCountdown}
                          className="absolute -top-4 right-0 mono text-[9px] tabular-nums pointer-events-none"
                          style={{
                            animation: "csv-auto-pin-countdown-flash 2s linear forwards",
                          }}
                          aria-live="polite"
                        >
                          {csvAutoPinCountdown}s
                        </span>
                      </>
                    )}
                    {/* Iter 66 — Countdown Halt Flash. Brief red chip
                        shown for ~400ms after the user cancels a
                        running auto-pin timer with mouseleave so they
                        see they saved the pin from firing. */}
                    {csvAutoPinHalt && (
                      <span
                        data-testid="csv-auto-pin-halt-flash"
                        className="absolute -top-4 right-0 mono text-[9px] tabular-nums text-red-300 pointer-events-none animate-in fade-in duration-150"
                        aria-live="polite"
                      >
                        canceled
                      </span>
                    )}
                    <div
                      data-testid="undo-analytics-csv-preview"
                      data-pinned={csvPreviewPinned ? "true" : "false"}
                      data-auto-pinned={csvAutoPinned ? "true" : "false"}
                      className={
                        "absolute right-0 top-full mt-1 z-50 bg-zinc-950 border border-zinc-700 rounded-sm shadow-lg p-2 w-[360px] text-left " +
                        (csvPreviewPinned ? "block" : "hidden group-hover:block")
                      }
                    >
                      {/* Iter 61 — CSV Preview Pin. Locks the modal open so
                          admins can compare the top-3 rows to the
                          sparklines without losing hover state. Clicking
                          again unpins. stopPropagation prevents the
                          outer Export button click.
                          Iter 62 — Auto-Pin On Long Hover. After 2s of
                          continuous hover on the trigger chip the
                          modal auto-pins itself and shows the
                          ``auto-pinned-hint`` chip so admins know why
                          it stayed open. Explicit pin toggle clears
                          the auto flag. */}
                      <div className="flex items-center justify-between mb-1 gap-2">
                        <div className="mono text-[9px] text-zinc-500 uppercase tracking-widest">
                          First 3 rows preview
                        </div>
                        <div className="flex items-center gap-1.5">
                          {csvPreviewPinned && csvAutoPinned && (
                            <span
                              data-testid="csv-preview-auto-pinned-hint"
                              className="mono text-[8px] uppercase tracking-widest text-amber-300/80 border border-amber-500/40 bg-amber-500/5 rounded-sm px-1 py-[1px]"
                              title="Auto-pinned after 2s of hover — click the pin to release"
                            >
                              Auto-pinned
                            </span>
                          )}
                          <span
                            role="button"
                            tabIndex={0}
                            data-testid="csv-preview-pin-btn"
                            data-pinned={csvPreviewPinned ? "true" : "false"}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              cancelCsvAutoPinTimer();
                              setCsvPreviewPinned((v) => !v);
                              setCsvAutoPinned(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                e.stopPropagation();
                                cancelCsvAutoPinTimer();
                                setCsvPreviewPinned((v) => !v);
                                setCsvAutoPinned(false);
                              }
                            }}
                            title={csvPreviewPinned ? "Unpin preview" : "Pin preview open"}
                            className={
                              "inline-flex items-center justify-center p-0.5 rounded-sm border transition-colors cursor-pointer select-none " +
                              (csvPreviewPinned
                                ? "border-amber-500/60 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20"
                                : "border-zinc-700 bg-transparent text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200")
                            }
                          >
                            {csvPreviewPinned
                              ? <PinOffIcon size={11} strokeWidth={2} />
                              : <PinIcon size={11} strokeWidth={2} />
                            }
                          </span>
                        </div>
                      </div>
                      <table className="w-full mono text-[9px] tabular-nums text-zinc-400">
                        <thead className="text-zinc-600">
                          <tr>
                            <th className="text-left pr-2">kind</th>
                            <th className="text-left pr-2">date</th>
                            <th className="text-right pr-1">shown</th>
                            <th className="text-right pr-1">click</th>
                            <th className="text-right pr-1">ext</th>
                            <th className="text-right pr-1">exp</th>
                            <th className="text-right">ctr%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewRows.map((row, i) => (
                            <tr key={`csv-prev-${i}`} data-testid={`csv-preview-row-${i}`}>
                              <td className="text-left pr-2 truncate">{row[0]}</td>
                              <td className="text-left pr-2">{row[1]}</td>
                              <td className="text-right pr-1">{row[2]}</td>
                              <td className="text-right pr-1">{row[3]}</td>
                              <td className="text-right pr-1">{row[4]}</td>
                              <td className="text-right pr-1">{row[5]}</td>
                              <td className="text-right">{row[6]}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </span>
                );
              })()}
            </button>
          </div>
        }
      />
      {data ? (
        <div className="p-5 space-y-4" data-testid="undo-events-analytics-body">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <DigestStat label="Toasts shown" value={data.totals.shown} />
            <DigestStat
              label="Undo clicked"
              value={data.totals.clicked}
              tone={data.totals.clicked > 0 ? "profit" : "default"}
            />
            <DigestStat
              label="Expired"
              value={data.totals.expired}
              tone={data.totals.expired > 0 ? "loss" : "default"}
            />
            <DigestStat
              label="Overall CTR"
              value={`${data.totals.ctr_pct}%`}
              tone={data.totals.ctr_pct >= 30 ? "profit" : "loss"}
            />
            <DigestStat
              label="Auto-extend rate"
              value={`${data.totals.extend_pct ?? 0}%`}
              tone={(data.totals.extend_pct ?? 0) > 20 ? "loss" : "default"}
            />
          </div>
          {/* Iter 56 — Extend threshold slider. 0 disables auto-extend
              entirely; up to 5s per backend clamp. */}
          <div className="flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2.5 gap-4">
            <div className="pr-4">
              <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Auto-extend trigger threshold</div>
              <div className="text-xs text-zinc-400 mt-1 leading-snug">
                Fires when the user hovers with ≤ this many seconds remaining. Set to 0 to disable.
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 w-40 shrink-0">
              <div className="mono text-[9px] text-zinc-500 uppercase tracking-widest w-full flex justify-between">
                <span>Threshold</span>
                <span data-testid="extend-threshold-value" className="text-zinc-300 tabular-nums">{threshold}s</span>
              </div>
              <Slider
                data-testid="extend-threshold-slider"
                min={0} max={5} step={1}
                value={[threshold]}
                onValueChange={onThresholdChange}
                className="w-full"
              />
            </div>
          </div>
          {/* Iter 57-78 — Threshold A/B history card extracted to its
              own component in Iter 78 (see ThresholdHistoryCard.jsx). */}
          <ThresholdHistoryCard
            history={history}
            setHistory={setHistory}
            proHintSeen={proHintSeen}
            setProHintSeen={setProHintSeen}
          />
          {data.breakdown.length > 0 && (
            <div className="border border-zinc-800 rounded-sm bg-zinc-950 divide-y divide-zinc-800/60">
              {data.breakdown.map((b) => {
                const shownSeries = (b.daily || []).map((d) => d.shown);
                return (
                  <div
                    key={`undo-kind-${b.kind}`}
                    data-testid={`undo-kind-${b.kind}`}
                    className="flex items-center justify-between px-3 py-2 mono text-xs"
                  >
                    <span className="text-zinc-300">{KIND_LABELS[b.kind] || b.kind}</span>
                    <div className="flex items-center gap-4 text-[10px] text-zinc-500 uppercase tracking-widest tabular-nums">
                      <span data-testid={`undo-kind-${b.kind}-sparkline`}>
                        <MiniSparklineSVG
                          values={shownSeries}
                          up={b.ctr_pct >= 30}
                          width={80}
                          height={14}
                          ariaLabel={`${b.kind} 7-day shown trend`}
                        />
                      </span>
                      <span>{b.shown} shown</span>
                      <span className="profit">{b.clicked} clicked</span>
                      <span className={b.expired > 0 ? "loss" : "text-zinc-500"}>{b.expired} expired</span>
                      <span
                        data-testid={`undo-kind-${b.kind}-extend`}
                        className={(b.extend_pct ?? 0) > 20 ? "loss" : "text-zinc-500"}
                      >
                        {b.extended ?? 0} extended ({b.extend_pct ?? 0}%)
                      </span>
                      <span
                        data-testid={`undo-kind-${b.kind}-ctr`}
                        className={b.ctr_pct >= 30 ? "profit" : "loss"}
                      >
                        {b.ctr_pct}% CTR
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="px-5 py-6 mono text-xs text-zinc-600">Loading analytics…</div>
      )}
    </Card>
  );
}
